import { useEffect, useMemo, useState } from "react";
import "./index.css";

const API_BASE =
  process.env.REACT_APP_PLAYER_API_BASE ||
  process.env.REACT_APP_PROXY_BASE ||
  "http://127.0.0.1:8000";

const TEAM_ABBRS = [
  "ATL", "BOS", "BKN", "CHA", "CHI", "CLE", "DAL", "DEN", "DET", "GSW",
  "HOU", "IND", "LAC", "LAL", "MEM", "MIA", "MIL", "MIN", "NOP", "NYK",
  "OKC", "ORL", "PHI", "PHX", "POR", "SAC", "SAS", "TOR", "UTA", "WAS",
];

const STATS = ["PTS", "REB", "AST", "3PM", "PRA", "PR", "PA", "RA", "MIN", "STL", "BLK", "TOV"];
const WINDOWS = [3, 5, 10, 15, 20];
const SEASON_TYPES = ["All", "Regular Season", "Playoffs"];
const SEASONS = (() => {
  const now = new Date();
  const currentYear = now.getMonth() >= 9 ? now.getFullYear() : now.getFullYear() - 1;
  const list = [];
  for (let y = currentYear; y >= 2017; y--) {
    list.push(`${y}-${String(y + 1).slice(2)}`);
  }
  return list;
})();
const STORAGE_KEY = "courtlogic.playerView.v1";
const TEAM_STRENGTHS = [
  { value: "", label: "All teams" },
  { value: "better", label: "Better / top 10" },
  { value: "similar", label: "Similar / middle 10" },
  { value: "worse", label: "Worse / bottom 10" },
];

function CourtLogicBrand() {
  return (
    <div className="brand-lockup" aria-label="CourtLogic">
      <img className="brand-logo" src="/icon-192.png" alt="CourtLogic" />
      <div>
        <div className="brand-name">CourtLogic</div>
        <div className="brand-subtitle">NBA Analytics Platform</div>
      </div>
    </div>
  );
}

function formatNumber(value, digits = 1) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
  return Number(value).toFixed(digits);
}

function formatPercent(value, digits = 0) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
  return `${Number(value).toFixed(digits)}%`;
}

function loadStoredView() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
  } catch (err) {
    return {};
  }
}

async function fetchJson(path, signal) {
  try {
    const response = await fetch(`${API_BASE}${path}`, { signal });
    if (!response.ok) {
      const detail = await response.text().catch(() => "");
      throw new Error(detail || `Request failed with ${response.status}`);
    }
    return response.json();
  } catch (err) {
    if (err.name === "AbortError") throw err;
    throw new Error(`Unable to reach CourtLogic API at ${API_BASE}. Start the FastAPI backend, then retry.`);
  }
}

function usePlayerSearch(query) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      const q = query.trim();
      if (q.length < 1) {
        setPlayers([]);
        setLoading(false);
        setError("");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const path = `/api/players/active?q=${encodeURIComponent(q)}&match=name_prefix&limit=25`;
        setPlayers(await fetchJson(path, controller.signal));
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  return { players, loading, error };
}

function StatTile({ label, value, detail }) {
  return (
    <div className="stat-tile">
      <span>{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );
}

function LandingPage() {
  return (
    <div className="landing-page">
      <div className="landing-content">
        <p className="eyebrow">NBA Analytics Platform</p>
        <h1>CourtLogic</h1>
        <p className="landing-tagline">Player profiles, recent form, splits, shot maps, and analysis — built from the player outward.</p>
        <div className="landing-prompt">
          <span className="landing-arrow">&#8592;</span>
          <span>Search for an active player to get started</span>
        </div>
        <div className="landing-features">
          <div className="landing-feature">
            <strong>Recent Form</strong>
            <span>Averages, medians, and trends over configurable game windows</span>
          </div>
          <div className="landing-feature">
            <strong>Season Profile</strong>
            <span>Efficiency baselines across any NBA season from 2017 onwards</span>
          </div>
          <div className="landing-feature">
            <strong>Splits & Filters</strong>
            <span>Slice by opponent, venue, result, date range, and defensive strength</span>
          </div>
          <div className="landing-feature">
            <strong>Shot Maps</strong>
            <span>Zone-level shooting breakdowns and shot distribution</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlayerHeader({ profile, selectedPlayer }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const profilePlayer = profile?.player || {};
  const player = {
    ...(selectedPlayer || {}),
    ...profilePlayer,
    full_name: profilePlayer.full_name || selectedPlayer?.full_name,
  };
  const season = profile?.season_stats || {};
  const hasProfile = Boolean(profile);
  const playerId = player.id || selectedPlayer?.id;
  const headshotUrl = playerId ? `https://cdn.nba.com/headshots/nba/latest/1040x760/${playerId}.png` : "";
  const profileDetails = [
    player.height && `${player.height}`,
    player.weight && `${player.weight} lb`,
    player.country,
    player.from_year && player.to_year && `${player.from_year}-${player.to_year}`,
  ].filter(Boolean);

  useEffect(() => {
    setImageLoaded(false);
  }, [playerId]);

  return (
    <section className="player-header">
      <div className="profile-main">
        <div className={imageLoaded ? "headshot-frame has-photo" : "headshot-frame"}>
          {headshotUrl ? (
            <img
              className="player-headshot"
              src={headshotUrl}
              alt={player.full_name || "NBA player"}
              onLoad={() => setImageLoaded(true)}
              onError={(event) => {
                setImageLoaded(false);
                event.currentTarget.style.display = "none";
              }}
            />
          ) : null}
          <span>{(player.full_name || "?").split(" ").map((part) => part[0]).join("").slice(0, 2)}</span>
        </div>
        <div>
          <p className="eyebrow gold">NBA Analytics Platform</p>
          <h1>{player.full_name || `Player ${player.id}`}</h1>
          <div className="meta-line">
            <span>{player.team_abbr || "Team TBD"}</span>
            <span>{player.position || "Position TBD"}</span>
            <span>{profile?.season || "Season loading"}</span>
            <span>{hasProfile ? `${profile.available_games || 0} games` : "Profile loading"}</span>
          </div>
          {profileDetails.length > 0 && (
            <div className="profile-details">
              {profileDetails.map((item) => <span key={item}>{item}</span>)}
            </div>
          )}
        </div>
      </div>
      <div className="header-stats">
        <StatTile label="PTS" value={formatNumber(season.points)} />
        <StatTile label="REB" value={formatNumber(season.rebounds)} />
        <StatTile label="AST" value={formatNumber(season.assists)} />
        <StatTile label="MIN" value={formatNumber(season.minutes)} />
      </div>
    </section>
  );
}

function ControlBar({
  stat,
  setStat,
  windowSize,
  setWindowSize,
  opponent,
  setOpponent,
  venue,
  setVenue,
  season,
  setSeason,
  seasonType,
  setSeasonType,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  winLoss,
  setWinLoss,
  defenseTier,
  setDefenseTier,
  offenseTier,
  setOffenseTier,
}) {
  return (
    <section className="control-bar">
      <label>
        <span>Stat</span>
        <select value={stat} onChange={(event) => setStat(event.target.value)}>
          {STATS.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </label>
      <label>
        <span>Window</span>
        <select value={windowSize} onChange={(event) => setWindowSize(Number(event.target.value))}>
          {WINDOWS.map((item) => <option key={item} value={item}>Last {item}</option>)}
        </select>
      </label>
      <label>
        <span>Season</span>
        <select value={season} onChange={(event) => setSeason(event.target.value)}>
          {SEASONS.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </label>
      <label>
        <span>Opponent</span>
        <select value={opponent} onChange={(event) => setOpponent(event.target.value)}>
          <option value="">All teams</option>
          {TEAM_ABBRS.map((team) => <option key={team} value={team}>{team}</option>)}
        </select>
      </label>
      <label>
        <span>Venue</span>
        <select value={venue} onChange={(event) => setVenue(event.target.value)}>
          <option value="">Home and away</option>
          <option value="home">Home</option>
          <option value="away">Away</option>
        </select>
      </label>
      <label>
        <span>Season Type</span>
        <select value={seasonType} onChange={(event) => setSeasonType(event.target.value)}>
          {SEASON_TYPES.map((item) => <option key={item} value={item}>{item === "All" ? "All games" : item}</option>)}
        </select>
      </label>
      <label>
        <span>From Date</span>
        <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
      </label>
      <label>
        <span>To Date</span>
        <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
      </label>
      <label>
        <span>Result</span>
        <select value={winLoss} onChange={(event) => setWinLoss(event.target.value)}>
          <option value="">Wins and losses</option>
          <option value="W">Wins</option>
          <option value="L">Losses</option>
        </select>
      </label>
      <label>
        <span>Opponent Defense</span>
        <select value={defenseTier} onChange={(event) => setDefenseTier(event.target.value)}>
          {TEAM_STRENGTHS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
      </label>
      <label>
        <span>Opponent Offense</span>
        <select value={offenseTier} onChange={(event) => setOffenseTier(event.target.value)}>
          {TEAM_STRENGTHS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
      </label>
    </section>
  );
}

function FilterChips({ stat, windowSize, season, opponent, venue, seasonType, dateFrom, dateTo, winLoss, defenseTier, offenseTier }) {
  const chips = [
    `Stat ${stat}`,
    `Last ${windowSize}`,
    season,
    seasonType === "All" ? "All games" : seasonType,
    opponent && `vs ${opponent}`,
    venue && venue,
    dateFrom && `from ${dateFrom}`,
    dateTo && `to ${dateTo}`,
    winLoss && (winLoss === "W" ? "wins only" : "losses only"),
    defenseTier && `${defenseTier} defenses`,
    offenseTier && `${offenseTier} offenses`,
  ].filter(Boolean);
  return (
    <div className="filter-chips">
      {chips.map((chip) => <span key={chip}>{chip}</span>)}
    </div>
  );
}

function PlayerSearch({ query, setQuery, selectedId, onSelectPlayer, players, loading, error }) {
  const canSearch = query.trim().length >= 1;
  return (
    <aside className="sidebar">
      <CourtLogicBrand />
      <div>
        <p className="eyebrow">Select Player</p>
        <h2>Active roster search</h2>
      </div>
      <input
        className="search-input"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search active players"
      />
      {error && <div className="inline-error">{error}</div>}
      <div className="player-list">
        {!canSearch && <div className="list-note">Type a player surname to search active players.</div>}
        {canSearch && loading && <div className="list-note">Searching players...</div>}
        {canSearch && !loading && players.length === 0 && (
          <div className="list-note">No active players matched that search.</div>
        )}
        {canSearch && !loading && players.map((player) => (
          <button
            key={player.id}
            className={selectedId === player.id ? "player-option active" : "player-option"}
            onClick={() => onSelectPlayer(player)}
          >
            <span>{player.full_name}</span>
            <small>#{player.id}</small>
          </button>
        ))}
      </div>
    </aside>
  );
}

function RecentPanel({ logs, stat, windowSize }) {
  const summary = logs?.summary || {};
  const games = logs?.games || [];
  const sampleWarning = summary.games > 0 && summary.games < Math.min(windowSize, 5);
  return (
    <section className="panel">
      <div className="panel-title">
        <div>
          <p className="eyebrow">Recent Form</p>
          <h2>{stat} over selected sample</h2>
        </div>
        <span className={sampleWarning ? "badge warn" : "badge"}>{summary.games || 0} games</span>
      </div>
      <div className="stat-grid">
        <StatTile label="Average" value={formatNumber(summary.average)} />
        <StatTile label="Median" value={formatNumber(summary.median)} />
        <StatTile label="High" value={formatNumber(summary.max)} />
        <StatTile label="Minutes" value={formatNumber(summary.minutes)} />
      </div>
      {sampleWarning && <p className="note">Small sample. Treat this split as context only.</p>}
      <div className="mini-bars">
        {games.slice().reverse().map((game, index) => {
          const value = Number(game.STAT_VALUE || 0);
          const max = Math.max(...games.map((item) => Number(item.STAT_VALUE || 0)), 1);
          return (
            <div className="bar-wrap" key={`${game.GAME_DATE}-${index}`}>
              <div className="bar" style={{ height: `${Math.max(8, (value / max) * 100)}%` }} />
              <span>{formatNumber(value, 0)}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SeasonProfilePanel({ profile }) {
  const stats = profile?.season_stats || {};
  const creationLoad = (
    Number(stats.field_goal_attempts || 0) +
    Number(stats.free_throw_attempts || 0) * 0.44 +
    Number(stats.assists || 0) +
    Number(stats.turnovers || 0)
  );
  return (
    <section className="panel season-panel">
      <div className="panel-title">
        <div>
          <p className="eyebrow">Season Profile</p>
          <h2>Role and efficiency baseline</h2>
        </div>
        <span className="badge">{stats.games || 0} games</span>
      </div>
      <div className="context-grid">
        <StatTile label="FG%" value={formatPercent(Number(stats.field_goal_pct) * 100, 1)} />
        <StatTile label="3P%" value={formatPercent(Number(stats.three_point_pct) * 100, 1)} />
        <StatTile label="FTA" value={formatNumber(stats.free_throw_attempts)} />
        <StatTile label="FGA" value={formatNumber(stats.field_goal_attempts)} />
        <StatTile label="TOV" value={formatNumber(stats.turnovers)} />
        <StatTile label="STL" value={formatNumber(stats.steals)} />
        <StatTile label="BLK" value={formatNumber(stats.blocks)} />
        <StatTile label="Load" value={formatNumber(creationLoad)} detail="FGA + .44 FTA + AST + TOV" />
      </div>
    </section>
  );
}

function SplitsPanel({ logs }) {
  const venue = logs?.splits?.venue || {};
  const result = logs?.splits?.result || {};
  const rest = logs?.splits?.rest || {};
  return (
    <section className="panel">
      <div className="panel-title">
        <div>
          <p className="eyebrow">Context</p>
          <h2>Home and away splits</h2>
        </div>
      </div>
      <div className="split-grid">
        {["home", "away"].map((key) => {
          const item = venue[key] || {};
          return (
            <div className="split-row" key={key}>
              <span>{key}</span>
              <strong>{formatNumber(item.average)}</strong>
              <small>{item.games || 0} games</small>
            </div>
          );
        })}
        {["w", "l"].map((key) => {
          const item = result[key] || {};
          return (
            <div className="split-row" key={key}>
              <span>{key === "w" ? "wins" : "losses"}</span>
              <strong>{formatNumber(item.average)}</strong>
              <small>{item.games || 0} games</small>
            </div>
          );
        })}
        {["0 days", "1 day", "2+ days"].map((key) => {
          const item = rest[key] || {};
          return (
            <div className="split-row" key={key}>
              <span>{key} rest</span>
              <strong>{formatNumber(item.average)}</strong>
              <small>{item.games || 0} games</small>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function MonthlyTrendPanel({ logs }) {
  const month = logs?.splits?.month || {};
  const rows = Object.entries(month).sort(([a], [b]) => a.localeCompare(b));
  return (
    <section className="panel trend-panel">
      <div className="panel-title">
        <div>
          <p className="eyebrow">Trend</p>
          <h2>Monthly split</h2>
        </div>
      </div>
      <div className="trend-list">
        {rows.length === 0 && <p className="note">No monthly split available for this sample.</p>}
        {rows.map(([key, item]) => (
          <div className="trend-row" key={key}>
            <span>{key}</span>
            <strong>{formatNumber(item.average)}</strong>
            <small>{item.games || 0} games</small>
          </div>
        ))}
      </div>
    </section>
  );
}

function GameLogTable({ logs }) {
  const games = logs?.games || [];
  return (
    <section className="panel table-panel">
      <div className="panel-title">
        <div>
          <p className="eyebrow">Evidence</p>
          <h2>Game log</h2>
        </div>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Matchup</th>
              <th>MIN</th>
              <th>PTS</th>
              <th>REB</th>
              <th>AST</th>
              <th>3PM</th>
              <th>Opp DEF</th>
              <th>Opp OFF</th>
              <th>Selected</th>
            </tr>
          </thead>
          <tbody>
            {games.map((game, index) => (
              <tr key={`${game.GAME_DATE}-${index}`}>
                <td>{game.GAME_DATE}</td>
                <td>{game.SEASON_TYPE || "-"}</td>
                <td>{game.MATCHUP}</td>
                <td>{formatNumber(game.MIN)}</td>
                <td>{formatNumber(game.PTS)}</td>
                <td>{formatNumber(game.REB)}</td>
                <td>{formatNumber(game.AST)}</td>
                <td>{formatNumber(game.FG3M)}</td>
                <td>{game.OPP_DEF_RANK ? `#${formatNumber(game.OPP_DEF_RANK, 0)}` : "-"}</td>
                <td>{game.OPP_OFF_RANK ? `#${formatNumber(game.OPP_OFF_RANK, 0)}` : "-"}</td>
                <td>{formatNumber(game.STAT_VALUE)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function GameContextPanel({ logs, profile }) {
  const games = logs?.games || [];
  const summary = logs?.summary || {};
  const stat = logs?.filters?.stat || "PTS";
  const season = profile?.season_stats || {};
  const seasonAverageByStat = {
    PTS: season.points,
    REB: season.rebounds,
    AST: season.assists,
    "3PM": season.threes,
    MIN: season.minutes,
  };
  const seasonAverage = seasonAverageByStat[stat];
  const values = games.map((game) => Number(game.STAT_VALUE)).filter((value) => !Number.isNaN(value));
  const firstHalf = values.slice(0, Math.ceil(values.length / 2));
  const secondHalf = values.slice(Math.ceil(values.length / 2));
  const average = (items) => {
    if (!items.length) return null;
    return items.reduce((total, value) => total + value, 0) / items.length;
  };
  const recentAvg = average(firstHalf);
  const priorAvg = average(secondHalf);
  const trend = recentAvg !== null && priorAvg !== null ? recentAvg - priorAvg : null;
  const hitRate = seasonAverage
    ? values.filter((value) => value >= Number(seasonAverage)).length / Math.max(values.length, 1) * 100
    : null;
  const playoffGames = games.filter((game) => game.SEASON_TYPE === "Playoffs").length;
  const regularGames = games.filter((game) => game.SEASON_TYPE === "Regular Season").length;
  const avgDefRank = average(games.map((game) => Number(game.OPP_DEF_RANK)).filter((value) => !Number.isNaN(value)));
  const avgOffRank = average(games.map((game) => Number(game.OPP_OFF_RANK)).filter((value) => !Number.isNaN(value)));
  const venue = logs?.splits?.venue || {};
  const homeAvg = venue.home?.average;
  const awayAvg = venue.away?.average;
  const bestVenue = homeAvg && awayAvg
    ? `${homeAvg >= awayAvg ? "Home" : "Away"} by ${Math.abs(homeAvg - awayAvg).toFixed(1)} ${stat}`
    : "Needs both venues";
  const latest = games[0];

  return (
    <section className="panel game-context-panel">
      <div className="panel-title">
        <div>
          <p className="eyebrow">Player Context</p>
          <h2>What this sample says</h2>
        </div>
        <span className="badge">{games.length} games</span>
      </div>
      <div className="context-grid">
        <StatTile
          label="Vs Season Avg"
          value={formatPercent(hitRate)}
          detail={seasonAverage ? `>= ${formatNumber(seasonAverage)} ${stat}` : "season baseline needed"}
        />
        <StatTile
          label="Recent Trend"
          value={trend === null ? "-" : `${trend >= 0 ? "+" : ""}${formatNumber(trend)}`}
          detail="front half vs back half"
        />
        <StatTile
          label="Opp DEF Rank"
          value={avgDefRank === null ? "-" : `#${formatNumber(avgDefRank, 0)}`}
          detail="average opponent"
        />
        <StatTile
          label="Opp OFF Rank"
          value={avgOffRank === null ? "-" : `#${formatNumber(avgOffRank, 0)}`}
          detail="average opponent"
        />
      </div>
      <div className="context-notes">
        <div>
          <span>Game mix</span>
          <strong>{playoffGames} Playoffs / {regularGames} Regular</strong>
        </div>
        <div>
          <span>Venue lean</span>
          <strong>{bestVenue}</strong>
        </div>
        <div>
          <span>Latest game</span>
          <strong>{latest ? `${latest.GAME_DATE} ${latest.MATCHUP} (${formatNumber(latest.STAT_VALUE)} ${stat})` : "No games in sample"}</strong>
        </div>
        <div>
          <span>Distribution</span>
          <strong>Low {formatNumber(summary.min)} / Median {formatNumber(summary.median)} / High {formatNumber(summary.max)}</strong>
        </div>
      </div>
    </section>
  );
}

function ShotMap({ shotChart }) {
  const shots = shotChart?.shots || [];
  const zones = shotChart?.zones || [];
  const tendencies = shotChart?.tendencies || [];
  return (
    <section className="panel shot-panel">
      <div className="panel-title">
        <div>
          <p className="eyebrow">Shot Profile</p>
          <h2>Shot map and tendencies</h2>
        </div>
        <span className="badge">{shots.length} shots</span>
      </div>
      <div className="shot-layout">
        <svg className="court" viewBox="-250 -50 500 470" role="img" aria-label="Basketball shot chart">
          <rect x="-250" y="-50" width="500" height="470" />
          <line x1="-220" y1="420" x2="220" y2="420" />
          <path d="M -80 0 A 80 80 0 0 0 80 0" />
          <circle cx="0" cy="0" r="7" />
          <rect x="-80" y="-50" width="160" height="190" />
          <path d="M -237 89 A 237 237 0 0 0 237 89" />
          {shots.map((shot, index) => {
            const x = Number(shot.LOC_X || 0);
            const y = Number(shot.LOC_Y || 0);
            const made = Number(shot.SHOT_MADE_FLAG) === 1;
            return <circle key={index} cx={x} cy={y} r="4" className={made ? "made" : "missed"} />;
          })}
        </svg>
        <div className="zone-list">
          {zones.slice(0, 6).map((zone) => (
            <div className="zone-row" key={zone.zone}>
              <span>{zone.zone}</span>
              <strong>{zone.attempts} FGA</strong>
              <small>{formatNumber(zone.fg_pct)}% FG</small>
            </div>
          ))}
          {tendencies.length > 0 && (
            <div className="tendency-list">
              {tendencies.map((item) => (
                <div className="tendency" key={`${item.label}-${item.value}`}>
                  <span>{item.label}</span>
                  <small>{item.value}</small>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function InsightPanel({ profile, logs, shotChart }) {
  const insights = useMemo(() => {
    if (!profile || !logs) return [];
    const season = profile.season_stats || {};
    const summary = logs.summary || {};
    const output = [];
    if (summary.average && season.points && logs.filters?.stat === "PTS") {
      const delta = summary.average - season.points;
      output.push(`Selected sample is ${Math.abs(delta).toFixed(1)} points ${delta >= 0 ? "above" : "below"} season scoring average.`);
    }
    if (summary.minutes && season.minutes) {
      const delta = summary.minutes - season.minutes;
      output.push(`Minutes are ${Math.abs(delta).toFixed(1)} ${delta >= 0 ? "above" : "below"} the season baseline.`);
    }
    const topZone = shotChart?.zones?.[0];
    if (topZone) {
      output.push(`${topZone.zone} is the highest-volume zone at ${topZone.attempts} attempts.`);
    }
    if ((summary.games || 0) < 5) {
      output.push("Current filters produce a small sample, so confidence should stay low.");
    }
    return output;
  }, [profile, logs, shotChart]);

  return (
    <section className="panel">
      <div className="panel-title">
        <div>
          <p className="eyebrow">Readout</p>
          <h2>Analysis notes</h2>
        </div>
      </div>
      <div className="insights">
        {insights.length === 0 && <p className="note">Select a player to generate factual analysis notes.</p>}
        {insights.map((item) => <p key={item}>{item}</p>)}
      </div>
    </section>
  );
}

function PlayStylePanel({ playStyle }) {
  const primary = playStyle?.primary_style || {};
  const secondary = playStyle?.secondary_styles || [];
  const metrics = playStyle?.metrics || {};
  const evidence = playStyle?.evidence || [];
  return (
    <section className="panel playstyle-panel">
      <div className="panel-title">
        <div>
          <p className="eyebrow">Play Style</p>
          <h2>{primary.label || "Classifying role"}</h2>
        </div>
        <span className="badge">{metrics.games || 0} games</span>
      </div>
      <div className="style-tags">
        {secondary.length === 0 && <span>Role evidence loading</span>}
        {secondary.map((item) => <span key={item.label}>{item.label}</span>)}
      </div>
      <div className="context-grid compact">
        <StatTile label="Rim Share" value={formatPercent(metrics.rim_attempt_share, 1)} />
        <StatTile label="3PA Rate" value={formatPercent(metrics.three_point_attempt_rate, 1)} />
        <StatTile label="FTA Rate" value={formatPercent(metrics.free_throw_rate, 1)} />
        <StatTile label="Mid-Range" value={formatPercent(metrics.mid_range_attempt_share, 1)} />
      </div>
      <div className="evidence-list">
        {evidence.slice(0, 4).map((item) => <p key={item}>{item}</p>)}
        {playStyle?.sample_notes?.map((item) => <p className="note-line" key={item}>{item}</p>)}
      </div>
    </section>
  );
}

function TeamRatingsPanel({ ratings }) {
  const teams = ratings?.teams || [];
  return (
    <section className="panel team-ratings-panel">
      <div className="panel-title">
        <div>
          <p className="eyebrow">League Context</p>
          <h2>Team ratings</h2>
        </div>
        <span className="badge">{ratings?.season || "Season"}</span>
      </div>
      <div className="ratings-grid">
        <div>
          <h3>Defense</h3>
          <div className="rating-list">
            {teams.slice().sort((a, b) => (a.def_rank || 99) - (b.def_rank || 99)).map((team) => (
              <div className={`rating-row ${team.defense_tier || ""}`} key={`def-${team.abbr}`}>
                <span>{team.def_rank}</span>
                <strong>{team.abbr}</strong>
                <small>{formatNumber(team.def_rating)} DEF</small>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3>Offense</h3>
          <div className="rating-list">
            {teams.slice().sort((a, b) => (a.off_rank || 99) - (b.off_rank || 99)).map((team) => (
              <div className={`rating-row ${team.offense_tier || ""}`} key={`off-${team.abbr}`}>
                <span>{team.off_rank}</span>
                <strong>{team.abbr}</strong>
                <small>{formatNumber(team.off_rating)} OFF</small>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PlayerComparePanel({
  selectedId,
  stat,
  windowSize,
  opponent,
  venue,
  season,
  seasonType,
  dateFrom,
  dateTo,
  winLoss,
  defenseTier,
  offenseTier,
}) {
  const [query, setQuery] = useState("");
  const [comparePlayer, setComparePlayer] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [compareError, setCompareError] = useState("");
  const { players, loading } = usePlayerSearch(query);
  const options = players.filter((player) => player.id !== selectedId);

  useEffect(() => {
    if (!selectedId || !comparePlayer) {
      setComparison(null);
      return;
    }
    const controller = new AbortController();
    async function loadComparison() {
      setLoadingComparison(true);
      setCompareError("");
      try {
        const params = new URLSearchParams({
          playerIds: `${selectedId},${comparePlayer.id}`,
          stat,
          last: String(windowSize),
          season,
          seasonType,
        });
        if (opponent) params.set("opponent", opponent);
        if (venue) params.set("home_away", venue);
        if (dateFrom) params.set("dateFrom", dateFrom);
        if (dateTo) params.set("dateTo", dateTo);
        if (winLoss) params.set("winLoss", winLoss);
        if (defenseTier) params.set("defenseTier", defenseTier);
        if (offenseTier) params.set("offenseTier", offenseTier);
        setComparison(await fetchJson(`/api/players/compare?${params.toString()}`, controller.signal));
      } catch (err) {
        if (err.name !== "AbortError") setCompareError(err.message);
      } finally {
        setLoadingComparison(false);
      }
    }
    loadComparison();
    return () => controller.abort();
  }, [selectedId, comparePlayer, stat, windowSize, opponent, venue, season, seasonType, dateFrom, dateTo, winLoss, defenseTier, offenseTier]);

  return (
    <section className="panel compare-panel">
      <div className="panel-title">
        <div>
          <p className="eyebrow">Player Comparison</p>
          <h2>Compare against another player</h2>
        </div>
        {comparePlayer && <span className="badge">{comparePlayer.full_name}</span>}
      </div>
      <div className="compare-search">
        <input
          className="search-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search comparison player"
        />
        {query.trim().length > 0 && (
          <div className="compare-options">
            {loading && <div className="list-note">Searching players...</div>}
            {!loading && options.slice(0, 6).map((player) => (
              <button
                type="button"
                key={player.id}
                className={comparePlayer?.id === player.id ? "player-option active" : "player-option"}
                onClick={() => {
                  setComparePlayer(player);
                  setQuery("");
                }}
              >
                <span>{player.full_name}</span>
                <small>#{player.id}</small>
              </button>
            ))}
          </div>
        )}
      </div>
      {loadingComparison && <div className="loading-strip">Loading comparison...</div>}
      {compareError && <div className="inline-error">{compareError}</div>}
      <div className="compare-grid">
        {(comparison?.players || []).map((item) => (
          <div className="compare-card" key={item.player.id}>
            <div>
              <strong>{item.player.full_name || `Player ${item.player.id}`}</strong>
              <span>{item.player.team_abbr || "Team TBD"} / {item.player.position || "Position TBD"}</span>
            </div>
            <div className="compare-stats">
              <StatTile label={`${stat} Sample`} value={formatNumber(item.recent_stats?.average)} detail={`${item.recent_stats?.games || 0} games`} />
              <StatTile label="Season PTS" value={formatNumber(item.season_stats?.points)} />
              <StatTile label="Minutes" value={formatNumber(item.recent_stats?.minutes)} detail={`season ${formatNumber(item.season_stats?.minutes)}`} />
            </div>
          </div>
        ))}
      </div>
      {comparison?.comparison_notes?.length > 0 && (
        <div className="evidence-list">
          {comparison.comparison_notes.map((item) => <p key={item}>{item}</p>)}
        </div>
      )}
      {!comparePlayer && <p className="note">Select a second player to compare both players under the active filters.</p>}
    </section>
  );
}

export default function App() {
  const storedView = useMemo(() => loadStoredView(), []);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [profile, setProfile] = useState(null);
  const [logs, setLogs] = useState(null);
  const [shotChart, setShotChart] = useState(null);
  const [playStyle, setPlayStyle] = useState(null);
  const [stat, setStat] = useState(storedView.stat || "PTS");
  const [windowSize, setWindowSize] = useState(storedView.windowSize || 10);
  const [opponent, setOpponent] = useState(storedView.opponent || "");
  const [venue, setVenue] = useState(storedView.venue || "");
  const [season, setSeason] = useState(storedView.season || SEASONS[0]);
  const [seasonType, setSeasonType] = useState(storedView.seasonType || "All");
  const [dateFrom, setDateFrom] = useState(storedView.dateFrom || "");
  const [dateTo, setDateTo] = useState(storedView.dateTo || "");
  const [winLoss, setWinLoss] = useState(storedView.winLoss || "");
  const [defenseTier, setDefenseTier] = useState(storedView.defenseTier || "");
  const [offenseTier, setOffenseTier] = useState(storedView.offenseTier || "");
  const [teamRatings, setTeamRatings] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [dataError, setDataError] = useState("");
  const { players, loading, error } = usePlayerSearch(query);

  function selectPlayer(player) {
    setSelectedId(player.id);
    setSelectedPlayer(player);
    setProfile(null);
    setLogs(null);
    setShotChart(null);
    setPlayStyle(null);
    setDataError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  useEffect(() => {
    const state = {
      selectedPlayer,
      stat,
      windowSize,
      opponent,
      venue,
      season,
      seasonType,
      dateFrom,
      dateTo,
      winLoss,
      defenseTier,
      offenseTier,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    const params = new URLSearchParams();
    if (selectedId) params.set("player", selectedId);
    if (stat !== "PTS") params.set("stat", stat);
    if (windowSize !== 10) params.set("last", String(windowSize));
    if (opponent) params.set("opponent", opponent);
    if (venue) params.set("venue", venue);
    if (season !== SEASONS[0]) params.set("season", season);
    if (seasonType !== "All") params.set("seasonType", seasonType);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    if (winLoss) params.set("result", winLoss);
    if (defenseTier) params.set("defense", defenseTier);
    if (offenseTier) params.set("offense", offenseTier);
    const next = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
    window.history.replaceState(null, "", next);
  }, [selectedId, selectedPlayer, stat, windowSize, opponent, venue, season, seasonType, dateFrom, dateTo, winLoss, defenseTier, offenseTier]);

  useEffect(() => {
    const controller = new AbortController();
    async function loadTeamRatings() {
      try {
        setTeamRatings(await fetchJson("/api/players/team-ratings", controller.signal));
      } catch (err) {
        if (err.name !== "AbortError") setTeamRatings(null);
      }
    }
    loadTeamRatings();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    const controller = new AbortController();
    async function loadPlayer() {
      setLoadingData(true);
      setDataError("");
      try {
        const params = new URLSearchParams({
          stat,
          last: String(windowSize),
          season,
          seasonType,
        });
        if (opponent) params.set("opponent", opponent);
        if (venue) params.set("home_away", venue);
        if (dateFrom) params.set("dateFrom", dateFrom);
        if (dateTo) params.set("dateTo", dateTo);
        if (winLoss) params.set("winLoss", winLoss);
        if (defenseTier) params.set("defenseTier", defenseTier);
        if (offenseTier) params.set("offenseTier", offenseTier);
        const seasonParam = `season=${encodeURIComponent(season)}&seasonType=${encodeURIComponent(seasonType)}`;
        const [profileData, logsData, shotData, playStyleData] = await Promise.all([
          fetchJson(`/api/players/${selectedId}/profile?${seasonParam}`, controller.signal),
          fetchJson(`/api/players/${selectedId}/game-logs?${params.toString()}`, controller.signal),
          fetchJson(`/api/players/${selectedId}/shot-chart?${seasonParam}`, controller.signal),
          fetchJson(`/api/players/${selectedId}/play-style?${seasonParam}`, controller.signal),
        ]);
        setProfile(profileData);
        setLogs(logsData);
        setShotChart(shotData);
        setPlayStyle(playStyleData);
      } catch (err) {
        if (err.name !== "AbortError") setDataError(err.message);
      } finally {
        setLoadingData(false);
      }
    }
    loadPlayer();
    return () => controller.abort();
  }, [selectedId, stat, windowSize, opponent, venue, season, seasonType, dateFrom, dateTo, winLoss, defenseTier, offenseTier]);

  return (
    <main className="app-shell">
      <PlayerSearch
        query={query}
        setQuery={setQuery}
        selectedId={selectedId}
        onSelectPlayer={selectPlayer}
        players={players}
        loading={loading}
        error={error}
      />
      <section className="workspace">
        {!selectedId && <LandingPage />}
        {selectedId && (
          <>
            <PlayerHeader profile={profile} selectedPlayer={selectedPlayer} />
            <ControlBar
              stat={stat}
              setStat={setStat}
              windowSize={windowSize}
              setWindowSize={setWindowSize}
              opponent={opponent}
              setOpponent={setOpponent}
              venue={venue}
              setVenue={setVenue}
              season={season}
              setSeason={setSeason}
              seasonType={seasonType}
              setSeasonType={setSeasonType}
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
              winLoss={winLoss}
              setWinLoss={setWinLoss}
              defenseTier={defenseTier}
              setDefenseTier={setDefenseTier}
              offenseTier={offenseTier}
              setOffenseTier={setOffenseTier}
            />
            <FilterChips
              stat={stat}
              windowSize={windowSize}
              season={season}
              opponent={opponent}
              venue={venue}
              seasonType={seasonType}
              dateFrom={dateFrom}
              dateTo={dateTo}
              winLoss={winLoss}
              defenseTier={defenseTier}
              offenseTier={offenseTier}
            />
            {loadingData && <div className="loading-strip">Loading player database view...</div>}
            {dataError && <div className="inline-error">{dataError}</div>}
            <div className="dashboard-grid">
              <RecentPanel logs={logs} stat={stat} windowSize={windowSize} />
              <SeasonProfilePanel profile={profile} />
              <SplitsPanel logs={logs} />
              <InsightPanel profile={profile} logs={logs} shotChart={shotChart} />
              <PlayStylePanel playStyle={playStyle} />
              <PlayerComparePanel
                selectedId={selectedId}
                stat={stat}
                windowSize={windowSize}
                opponent={opponent}
                venue={venue}
                season={season}
                seasonType={seasonType}
                dateFrom={dateFrom}
                dateTo={dateTo}
                winLoss={winLoss}
                defenseTier={defenseTier}
                offenseTier={offenseTier}
              />
              <TeamRatingsPanel ratings={teamRatings} />
              <MonthlyTrendPanel logs={logs} />
              <ShotMap shotChart={shotChart} />
              <GameLogTable logs={logs} />
              <GameContextPanel logs={logs} profile={profile} />
            </div>
          </>
        )}
      </section>
    </main>
  );
}
